import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle, Building, UserCheck, GraduationCap, Paintbrush, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

// The 4 Solution Categories with detailed descriptions
const solutionCategories = [
  {
    id: 'unterweisungssystem',
    title: { de: 'Unterweisungssystem', en: 'Instruction System' },
    description: {
      de: 'Verwalten, organisieren und dokumentieren Sie alle Unterweisungen zentral – mit unserem digitalen Unterweisungssystem.',
      en: 'Manage, organize and document all instructions centrally – with our digital instruction system.'
    },
    longDescription: {
      de: 'Unser digitales Unterweisungssystem ermöglicht es Ihnen, alle Schulungen und Unterweisungen an einem zentralen Ort zu verwalten. Automatische Erinnerungen, Zertifikate und umfassende Dokumentation sorgen für lückenlose Nachweisführung.',
      en: 'Our digital instruction system allows you to manage all training and instructions in one central location. Automatic reminders, certificates, and comprehensive documentation ensure seamless record-keeping.'
    },
    icon: Building,
    image: '/assets/courses/smartphone_tablet_monitor.jpg',
    features: {
      de: ['Zentrale Verwaltung', 'Automatische Erinnerungen', 'Zertifikatserstellung', 'Lückenlose Dokumentation', 'Benutzerimport'],
      en: ['Central Management', 'Automatic Reminders', 'Certificate Generation', 'Complete Documentation', 'User Import']
    },
    hasDetailPage: true,
    detailLink: '/instruction-system'
  },
  {
    id: 'besucherunterweisung',
    title: { de: 'Besucherunterweisung', en: 'Visitor Instruction' },
    description: {
      de: 'Digitale Lösungen für die Unterweisung von Besuchern, Fremdfirmen und Lieferanten – einfach, effizient und rechtssicher.',
      en: 'Digital solutions for instructing visitors, contractors and suppliers – simple, efficient and legally compliant.'
    },
    longDescription: {
      de: 'Unterweisen Sie Besucher, Fremdfirmen und Lieferanten digital – vor dem Betreten Ihres Geländes oder direkt vor Ort. Rechtssichere Dokumentation aller Unterweisungen.',
      en: 'Instruct visitors, contractors and suppliers digitally – before entering your premises or directly on site. Legally compliant documentation of all instructions.'
    },
    icon: UserCheck,
    image: '/assets/courses/visitor-instruction.jpeg',
    features: {
      de: ['Vorab-Unterweisung', 'Vor-Ort-Terminals', 'QR-Code Anmeldung', 'Besucherverwaltung', 'Rechtssichere Dokumentation'],
      en: ['Pre-instruction', 'On-site Terminals', 'QR Code Registration', 'Visitor Management', 'Legally Compliant Documentation']
    },
    hasDetailPage: true,
    detailLink: '/visitor-system'
  },
  {
    id: 'etraining-inhalt',
    title: { de: 'Standardisierte E-Trainings', en: 'Standardized E-Trainings' },
    description: {
      de: 'Fertige digitale Trainings für Arbeitssicherheit, IT-Awareness, Compliance und betriebliche Weiterbildung – sofort einsatzbereit und rechtssicher.',
      en: 'Ready-made digital training for workplace safety, IT awareness, compliance and corporate training – ready to use and legally compliant.'
    },
    longDescription: {
      de: 'Unsere standardisierten E-Trainings decken ein breites Themenspektrum ab und sind sofort einsetzbar. Alle Module erfüllen die gesetzlichen Anforderungen und sind in mehreren Sprachen verfügbar.',
      en: 'Our standardized e-trainings cover a wide range of topics and are ready to use immediately. All modules meet legal requirements and are available in multiple languages.'
    },
    icon: GraduationCap,
    subcategories: { 
      de: ['Arbeitssicherheit', 'Compliance', 'Cyber Security Awareness Trainings (Fit4)'], 
      en: ['Workplace Safety', 'Compliance', 'Cyber Security Awareness Trainings (Fit4)'] 
    },
    image: '/assets/courses/brandschutz.webp',
    features: {
      de: ['Sofort einsatzbereit', 'Mehrsprachig', 'Gesetzeskonform', 'Anpassbar', 'Zertifikate'],
      en: ['Ready to Use', 'Multilingual', 'Legally Compliant', 'Customizable', 'Certificates']
    },
    hasCatalog: true
  },
  {
    id: 'elearning-produktion',
    title: { de: 'Individuelle E-Learnings', en: 'Custom E-Learnings' },
    description: {
      de: 'Wir entwickeln maßgeschneiderte digitale Trainings – abgestimmt auf Ihr Unternehmen, Ihre Prozesse und Ihre Inhalte.',
      en: 'We develop customized digital training – tailored to your company, your processes and your content.'
    },
    longDescription: {
      de: 'Sie haben spezielle Anforderungen? Wir entwickeln individuelle E-Learning-Module, die genau auf Ihre Unternehmensprozesse, Ihr Corporate Design und Ihre Inhalte zugeschnitten sind.',
      en: 'Do you have special requirements? We develop individual e-learning modules that are precisely tailored to your company processes, corporate design and content.'
    },
    icon: Paintbrush,
    image: '/assets/courses/fit4ai.webp',
    features: {
      de: ['Maßgeschneidert', 'Corporate Design', 'Ihre Prozesse', 'Ihre Inhalte', 'Full-Service'],
      en: ['Customized', 'Corporate Design', 'Your Processes', 'Your Content', 'Full-Service']
    }
  }
];

export default function SolutionsPage() {
  const { language } = useLanguage();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(null);

  // Scroll to hash on load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setActiveCategory(id);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen pt-20" data-testid="solutions-page">
      <SEO
        title={language === 'de' ? 'E-Learning Lösungen für Arbeitssicherheit & Compliance' : 'E-Learning Solutions for Workplace Safety & Compliance'}
        description={language === 'de' 
          ? 'Digitale E-Learning Lösungen: Unterweisungssystem, Besucherunterweisung, Arbeitssicherheit, Cyber Security Awareness und DSGVO Schulungen. SCORM-kompatibel und rechtssicher.' 
          : 'Digital e-learning solutions: Training system, visitor instruction, workplace safety, cyber security awareness and GDPR training. SCORM-compatible and legally compliant.'}
        keywords={language === 'de'
          ? 'E-Learning Lösungen, Unterweisungssystem, Besucherunterweisung, Arbeitssicherheit E-Learning, Cyber Security Awareness, DSGVO Schulung, SCORM, LMS Integration'
          : 'E-Learning Solutions, Training System, Visitor Instruction, Workplace Safety E-Learning, Cyber Security Awareness, GDPR Training, SCORM, LMS Integration'}
        path="/solutions"
        lang={language}
      />
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]" data-testid="solutions-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
              {language === 'de' ? 'Unsere Lösungen' : 'Our Solutions'}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0D2440] mb-6">
              {language === 'de' ? 'E-Learning für jeden Bedarf' : 'E-Learning for Every Need'}
            </h1>
            <p className="text-lg md:text-xl text-[#5A7A9B]">
              {language === 'de'
                ? 'Entdecken Sie unsere vielfältigen E-Learning-Lösungen für Compliance, Sicherheit und mehr.'
                : 'Discover our diverse e-learning solutions for compliance, safety and more.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4 Clickable Solution Badges */}
      <section className="py-8 bg-white border-b border-[#C8D8E8]" data-testid="solution-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {solutionCategories.map((solution, index) => (
              <motion.button
                key={solution.id}
                onClick={() => {
                  setActiveCategory(activeCategory === solution.id ? null : solution.id);
                  const element = document.getElementById(solution.id);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className={`flex items-center space-x-3 p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                  activeCategory === solution.id
                    ? 'border-[#2E5E99] bg-[#2E5E99]/5'
                    : 'border-[#C8D8E8] hover:border-[#2E5E99]/50 bg-white'
                }`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`badge-${solution.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  activeCategory === solution.id ? 'bg-[#2E5E99]' : 'bg-[#2E5E99]/10'
                }`}>
                  <solution.icon className={`w-5 h-5 ${
                    activeCategory === solution.id ? 'text-white' : 'text-[#2E5E99]'
                  }`} />
                </div>
                <span className={`font-semibold text-sm ${
                  activeCategory === solution.id ? 'text-[#2E5E99]' : 'text-[#0D2440]'
                }`}>
                  {solution.title[language]}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Solution Sections */}
      {solutionCategories.map((solution, index) => (
        <section
          key={solution.id}
          id={solution.id}
          className={`py-20 scroll-mt-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#E7F0FA]'}`}
          data-testid={`section-${solution.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <motion.div {...fadeUp} className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                {/* Icon and Title */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-[#2E5E99]/10 rounded-xl flex items-center justify-center">
                    <solution.icon className="w-7 h-7 text-[#2E5E99]" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
                    {solution.title[language]}
                  </h2>
                </div>
                
                <p className="text-lg text-[#5A7A9B] mb-4">
                  {solution.description[language]}
                </p>
                
                <p className="text-[#5A7A9B] mb-8">
                  {solution.longDescription[language]}
                </p>

                {/* Subcategories if any */}
                {solution.subcategories && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-[#0D2440] mb-3">
                      {language === 'de' ? 'Themenbereiche:' : 'Topics:'}
                    </h4>
                    <ul className="space-y-2">
                      {solution.subcategories[language].map((sub, i) => (
                        <li key={`sub-${solution.id}-${i}`} className="flex items-center text-[#5A7A9B]">
                          <CheckCircle className="w-5 h-5 text-[#2E5E99] mr-2 flex-shrink-0" />
                          {sub}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {solution.features[language].map((feature, i) => (
                    <span
                      key={`feat-${solution.id}-${i}`}
                      className="px-3 py-1.5 bg-[#2E5E99]/10 text-[#2E5E99] text-sm font-medium rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  {solution.hasCatalog && (
                    <Link to="/produktkatalog">
                      <Button className="bg-[#2E5E99] hover:bg-[#1E4F85] text-white h-12 px-8">
                        {language === 'de' ? 'Produktkatalog' : 'Product Catalog'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                  {solution.hasDetailPage && (
                    <Link to={solution.detailLink}>
                      <Button className="bg-cyan-600 hover:bg-cyan-500 text-white h-12 px-8">
                        {language === 'de' ? 'Systemdetails' : 'System Details'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                  <Link to="/booking">
                    <Button variant={solution.hasCatalog || solution.hasDetailPage ? "outline" : "default"} className={solution.hasCatalog || solution.hasDetailPage ? "border-[#0D2440] text-[#0D2440] h-12 px-8" : "bg-[#0D2440] hover:bg-[#162D4A] text-white h-12 px-8"}>
                      {language === 'de' ? 'Demo / Anfrage' : 'Demo / Inquiry'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Image */}
              <motion.div
                className={index % 2 !== 0 ? 'lg:order-1' : ''}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={solution.image}
                    alt={solution.title[language]}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#2E5E99]" data-testid="solutions-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {language === 'de'
                ? 'Bereit für Ihre E-Learning-Lösung?'
                : 'Ready for Your E-Learning Solution?'}
            </h2>
            <p className="text-lg text-white/80 mb-10">
              {language === 'de'
                ? 'Kontaktieren Sie uns für eine unverbindliche Beratung.'
                : 'Contact us for a non-binding consultation.'}
            </p>
            <Link to="/contact" data-testid="solutions-contact-cta">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 h-12 px-8 text-base">
                {language === 'de' ? 'Kontakt aufnehmen' : 'Get in Touch'}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
